/**
 * Comprehensive Analytics Event Collector
 * Tracks user behavior, interactions, and journey data
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Session management
const SESSION_KEY = 'analytics_session';
const SESSION_DURATION = 30 * 60 * 1000; // 30 minutes

class AnalyticsCollector {
  constructor() {
    this.eventQueue = [];
    this.isProcessing = false;
    this.sessionId = this.getOrCreateSession();
    this.utmParams = this.captureUTMParams();
    this.referrer = document.referrer || 'direct';
    this.pageLoadTime = Date.now();
    this.scrollDepthTracked = { 25: false, 50: false, 75: false, 100: false };
    this.sectionTimes = {};
    this.clickCounts = {};
    this.lastClickTime = 0;
    this.lastClickPosition = { x: 0, y: 0 };
    this.rageClickThreshold = 3;
    this.rageClickTimeWindow = 1000; // 1 second
    this.rapidClicks = [];
    
    // Initialize tracking
    this.init();
  }

  init() {
    // Set up event listeners
    this.setupScrollTracking();
    this.setupClickTracking();
    this.setupVisibilityTracking();
    this.setupExitIntentTracking();
    this.setupUnloadTracking();
    
    // Start batch processing
    this.startBatchProcessing();
    
    // Start heartbeat for live tracking
    this.startHeartbeat();
    
    // Track initial page view
    this.trackPageView();
  }

  // Heartbeat for live tracking - sends current state every 3 seconds
  startHeartbeat() {
    this.heartbeatInterval = setInterval(() => {
      this.sendHeartbeat();
    }, 3000);
  }

  sendHeartbeat() {
    const currentScroll = Math.round((window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100) || 0;
    
    this.track('heartbeat', {
      scroll_position: currentScroll,
      time_on_page: Math.round((Date.now() - this.pageLoadTime) / 1000),
      viewport_height: window.innerHeight,
      page_height: document.documentElement.scrollHeight,
      is_active: !document.hidden
    }, true); // true = high priority, send immediately
  }

  // Session Management
  getOrCreateSession() {
    try {
      const stored = localStorage.getItem(SESSION_KEY);
      if (stored) {
        const session = JSON.parse(stored);
        if (Date.now() - session.lastActivity < SESSION_DURATION) {
          session.lastActivity = Date.now();
          session.isReturning = true;
          localStorage.setItem(SESSION_KEY, JSON.stringify(session));
          return session.id;
        }
      }
      
      // Create new session
      const newSession = {
        id: `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        startTime: Date.now(),
        lastActivity: Date.now(),
        isReturning: !!stored,
        pageViews: 0
      };
      localStorage.setItem(SESSION_KEY, JSON.stringify(newSession));
      return newSession.id;
    } catch (e) {
      return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }
  }

  updateSessionActivity() {
    try {
      const stored = localStorage.getItem(SESSION_KEY);
      if (stored) {
        const session = JSON.parse(stored);
        session.lastActivity = Date.now();
        session.pageViews = (session.pageViews || 0) + 1;
        localStorage.setItem(SESSION_KEY, JSON.stringify(session));
      }
    } catch (e) {
      console.error('Failed to update session:', e);
    }
  }

  // UTM Parameter Capture
  captureUTMParams() {
    const params = new URLSearchParams(window.location.search);
    const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
    const utmParams = {};
    
    utmKeys.forEach(key => {
      const value = params.get(key);
      if (value) utmParams[key] = value;
    });
    
    // Store UTM params for session persistence
    if (Object.keys(utmParams).length > 0) {
      try {
        localStorage.setItem('analytics_utm', JSON.stringify(utmParams));
      } catch (e) {}
    } else {
      try {
        const stored = localStorage.getItem('analytics_utm');
        if (stored) return JSON.parse(stored);
      } catch (e) {}
    }
    
    return utmParams;
  }

  // Device & Browser Info
  getDeviceInfo() {
    const ua = navigator.userAgent;
    const isMobile = /Mobile|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
    const isTablet = /iPad|Android(?!.*Mobile)/i.test(ua);
    
    let browser = 'Unknown';
    if (ua.includes('Chrome') && !ua.includes('Edg')) browser = 'Chrome';
    else if (ua.includes('Safari') && !ua.includes('Chrome')) browser = 'Safari';
    else if (ua.includes('Firefox')) browser = 'Firefox';
    else if (ua.includes('Edg')) browser = 'Edge';
    else if (ua.includes('Opera') || ua.includes('OPR')) browser = 'Opera';
    
    let os = 'Unknown';
    if (ua.includes('Windows')) os = 'Windows';
    else if (ua.includes('Mac OS')) os = 'MacOS';
    else if (ua.includes('Linux')) os = 'Linux';
    else if (ua.includes('Android')) os = 'Android';
    else if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';
    
    return {
      device_type: isTablet ? 'tablet' : (isMobile ? 'mobile' : 'desktop'),
      browser,
      os,
      screen_width: window.screen.width,
      screen_height: window.screen.height,
      viewport_width: window.innerWidth,
      viewport_height: window.innerHeight,
      language: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    };
  }

  // Core tracking method
  track(eventName, payload = {}, immediate = false) {
    const event = {
      event_name: eventName,
      event_payload: {
        ...payload,
        timestamp: Date.now()
      },
      session_id: this.sessionId,
      page_url: window.location.href,
      page_path: window.location.pathname,
      referrer: this.referrer,
      utm_params: this.utmParams,
      device_info: this.getDeviceInfo(),
      created_at: new Date().toISOString()
    };
    
    this.eventQueue.push(event);
    this.updateSessionActivity();
    
    // Send immediately for high-priority events (like heartbeat)
    if (immediate && this.eventQueue.length > 0) {
      this.flushEvents();
    }
  }

  // Page View Tracking
  trackPageView() {
    this.track('page_view', {
      page_title: document.title,
      page_path: window.location.pathname,
      query_string: window.location.search
    });
  }

  // Scroll Depth Tracking
  setupScrollTracking() {
    let ticking = false;
    
    const checkScrollDepth = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = Math.round((scrollTop / docHeight) * 100);
      
      [25, 50, 75, 100].forEach(depth => {
        if (scrollPercent >= depth && !this.scrollDepthTracked[depth]) {
          this.scrollDepthTracked[depth] = true;
          this.track('scroll_depth', { depth_percent: depth });
        }
      });
    };
    
    window.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          checkScrollDepth();
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });
  }

  // Section Time Tracking
  trackSectionView(sectionId, sectionName) {
    if (!this.sectionTimes[sectionId]) {
      this.sectionTimes[sectionId] = {
        name: sectionName,
        startTime: Date.now(),
        totalTime: 0,
        isVisible: true
      };
    } else {
      this.sectionTimes[sectionId].startTime = Date.now();
      this.sectionTimes[sectionId].isVisible = true;
    }
  }

  trackSectionLeave(sectionId) {
    if (this.sectionTimes[sectionId] && this.sectionTimes[sectionId].isVisible) {
      const timeSpent = Date.now() - this.sectionTimes[sectionId].startTime;
      this.sectionTimes[sectionId].totalTime += timeSpent;
      this.sectionTimes[sectionId].isVisible = false;
      
      this.track('section_time', {
        section_id: sectionId,
        section_name: this.sectionTimes[sectionId].name,
        time_spent_ms: timeSpent,
        total_time_ms: this.sectionTimes[sectionId].totalTime
      });
    }
  }

  // Click Tracking
  setupClickTracking() {
    document.addEventListener('click', (e) => {
      const target = e.target.closest('[data-track-click], a, button');
      if (!target) return;
      
      // Rage click detection
      this.detectRageClick(e);
      
      // Get tracking info
      const trackId = target.dataset.trackClick || target.dataset.testid || '';
      const tagName = target.tagName.toLowerCase();
      const text = target.innerText?.substring(0, 50) || '';
      const href = target.href || '';
      
      // Determine click type
      let clickType = 'generic';
      if (href.startsWith('tel:')) clickType = 'phone_click';
      else if (href.startsWith('mailto:')) clickType = 'email_click';
      else if (href.includes('wa.me') || href.includes('whatsapp')) clickType = 'whatsapp_click';
      else if (target.closest('[data-testid*="cta"]')) clickType = 'cta_click';
      else if (tagName === 'button') clickType = 'button_click';
      else if (tagName === 'a') clickType = 'link_click';
      
      this.track('click', {
        click_type: clickType,
        element_id: trackId,
        element_tag: tagName,
        element_text: text,
        element_href: href,
        position_x: e.clientX,
        position_y: e.clientY
      });
    }, { passive: true });
  }

  // Rage Click Detection
  detectRageClick(e) {
    const now = Date.now();
    const position = { x: e.clientX, y: e.clientY };
    
    // Remove old clicks outside time window
    this.rapidClicks = this.rapidClicks.filter(
      click => now - click.time < this.rageClickTimeWindow
    );
    
    // Add current click
    this.rapidClicks.push({ time: now, x: position.x, y: position.y });
    
    // Check for rage clicks (3+ clicks in same area within 1 second)
    if (this.rapidClicks.length >= this.rageClickThreshold) {
      const avgX = this.rapidClicks.reduce((sum, c) => sum + c.x, 0) / this.rapidClicks.length;
      const avgY = this.rapidClicks.reduce((sum, c) => sum + c.y, 0) / this.rapidClicks.length;
      
      // Check if all clicks are within 50px of average
      const isCluster = this.rapidClicks.every(c => 
        Math.abs(c.x - avgX) < 50 && Math.abs(c.y - avgY) < 50
      );
      
      if (isCluster) {
        this.track('rage_click', {
          click_count: this.rapidClicks.length,
          position_x: Math.round(avgX),
          position_y: Math.round(avgY),
          element: document.elementFromPoint(avgX, avgY)?.tagName || 'unknown'
        });
        this.rapidClicks = []; // Reset after detection
      }
    }
  }

  // Exit Intent Tracking (Desktop)
  setupExitIntentTracking() {
    if (this.getDeviceInfo().device_type !== 'desktop') return;
    
    let exitIntentShown = false;
    
    document.addEventListener('mouseout', (e) => {
      if (exitIntentShown) return;
      
      // Check if mouse left through the top
      if (e.clientY < 10 && e.relatedTarget === null) {
        exitIntentShown = true;
        this.track('exit_intent', {
          time_on_page_ms: Date.now() - this.pageLoadTime,
          scroll_depth: this.getMaxScrollDepth()
        });
      }
    });
  }

  getMaxScrollDepth() {
    const depths = [100, 75, 50, 25];
    for (const depth of depths) {
      if (this.scrollDepthTracked[depth]) return depth;
    }
    return 0;
  }

  // Visibility Tracking
  setupVisibilityTracking() {
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.track('page_hidden', {
          time_on_page_ms: Date.now() - this.pageLoadTime
        });
      } else {
        this.track('page_visible', {
          time_away_ms: Date.now() - this.pageLoadTime
        });
      }
    });
  }

  // Unload Tracking
  setupUnloadTracking() {
    const sendFinalEvents = () => {
      // Track page exit
      this.track('page_exit', {
        time_on_page_ms: Date.now() - this.pageLoadTime,
        scroll_depth: this.getMaxScrollDepth(),
        events_in_session: this.eventQueue.length
      });
      
      // Send remaining events synchronously
      this.flushEvents(true);
    };
    
    window.addEventListener('beforeunload', sendFinalEvents);
    window.addEventListener('pagehide', sendFinalEvents);
  }

  // Video Tracking
  trackVideoEvent(videoId, eventType, payload = {}) {
    this.track('video_interaction', {
      video_id: videoId,
      interaction_type: eventType, // play, pause, ended, progress
      ...payload
    });
  }

  // Form Tracking
  trackFormStart(formId, formName) {
    this.track('form_start', {
      form_id: formId,
      form_name: formName
    });
  }

  trackFormFieldInteraction(formId, fieldName, action) {
    this.track('form_field', {
      form_id: formId,
      field_name: fieldName,
      action: action // focus, blur, change, error
    });
  }

  trackFormSubmit(formId, formName, success, errorMessage = null) {
    this.track('form_submit', {
      form_id: formId,
      form_name: formName,
      success,
      error_message: errorMessage
    });
  }

  // Gallery Tracking
  trackGalleryInteraction(galleryId, action, payload = {}) {
    this.track('gallery_interaction', {
      gallery_id: galleryId,
      action: action, // open, close, next, prev, zoom
      ...payload
    });
  }

  // CTA Tracking
  trackCTAClick(ctaId, ctaText, ctaType) {
    this.track('cta_click', {
      cta_id: ctaId,
      cta_text: ctaText,
      cta_type: ctaType // enquire, whatsapp, call, download, check_availability
    });
  }

  // Batch Processing
  startBatchProcessing() {
    // Send events every 5 seconds
    setInterval(() => {
      this.flushEvents();
    }, 5000);
  }

  async flushEvents(sync = false) {
    if (this.eventQueue.length === 0 || this.isProcessing) return;
    
    this.isProcessing = true;
    const eventsToSend = [...this.eventQueue];
    this.eventQueue = [];
    
    try {
      if (sync && navigator.sendBeacon) {
        // Use sendBeacon for page unload
        navigator.sendBeacon(
          `${API}/analytics/batch`,
          JSON.stringify({ events: eventsToSend })
        );
      } else {
        await fetch(`${API}/analytics/batch`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ events: eventsToSend }),
          keepalive: true
        });
      }
    } catch (error) {
      // Re-queue events on failure
      this.eventQueue = [...eventsToSend, ...this.eventQueue];
      console.error('Failed to send analytics events:', error);
    } finally {
      this.isProcessing = false;
    }
  }

  // Link lead to session
  linkLeadToSession(leadId, leadInfo = {}) {
    try {
      const stored = localStorage.getItem(SESSION_KEY);
      if (stored) {
        const session = JSON.parse(stored);
        session.leadId = leadId;
        session.leadInfo = leadInfo;
        localStorage.setItem(SESSION_KEY, JSON.stringify(session));
      }
    } catch (e) {}
    
    this.track('lead_identified', {
      lead_id: leadId,
      ...leadInfo
    });
  }

  // Get session ID
  getSessionId() {
    return this.sessionId;
  }

  // Get visitor ID (persistent across sessions)
  getVisitorId() {
    try {
      let visitorId = localStorage.getItem('visitor_id');
      if (!visitorId) {
        visitorId = `visitor_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        localStorage.setItem('visitor_id', visitorId);
      }
      return visitorId;
    } catch (e) {
      return `visitor_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }
  }

  // Track filter/calculator changes for intent analysis
  trackFilterChange(filterType, value, previousValue) {
    this.track('filter_changed', {
      filter_type: filterType,
      new_value: value,
      previous_value: previousValue
    });

    // Also track specific filter types for intent analysis
    if (filterType === 'guest_count') {
      this.track('guest_count_changed', { count: value, previous: previousValue });
    } else if (filterType === 'duration') {
      this.track('duration_selected', { hours: value, previous: previousValue });
    } else if (filterType === 'payment_mode') {
      this.track('payment_mode_selected', { advance_percent: value, previous: previousValue });
    }
  }

  // Trigger intent analysis
  async analyzeIntent() {
    try {
      const response = await fetch(`${API}/intent/analyze?session_id=${this.sessionId}`, {
        method: 'POST'
      });
      if (response.ok) {
        const data = await response.json();
        return data;
      }
    } catch (error) {
      console.error('Failed to analyze intent:', error);
    }
    return null;
  }
}

// Create singleton instance
const analytics = new AnalyticsCollector();

// Export convenience methods
export const trackEvent = (name, payload) => analytics.track(name, payload);
export const trackPageView = () => analytics.trackPageView();
export const trackSectionView = (id, name) => analytics.trackSectionView(id, name);
export const trackSectionLeave = (id) => analytics.trackSectionLeave(id);
export const trackVideoEvent = (id, type, payload) => analytics.trackVideoEvent(id, type, payload);
export const trackFormStart = (id, name) => analytics.trackFormStart(id, name);
export const trackFormField = (formId, field, action) => analytics.trackFormFieldInteraction(formId, field, action);
export const trackFormSubmit = (id, name, success, error) => analytics.trackFormSubmit(id, name, success, error);
export const trackGallery = (id, action, payload) => analytics.trackGalleryInteraction(id, action, payload);
export const trackCTA = (id, text, type) => analytics.trackCTAClick(id, text, type);
export const linkLead = (leadId, info) => analytics.linkLeadToSession(leadId, info);
export const getSessionId = () => analytics.getSessionId();
export const getVisitorId = () => analytics.getVisitorId();
export const trackFilterChange = (type, value, prev) => analytics.trackFilterChange(type, value, prev);
export const analyzeIntent = () => analytics.analyzeIntent();

export default analytics;
